import styles from './interactions.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
} from 'antd';
import {GET_USER_INTERACTIONS} from 'graphql/queries/users';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {Button} from '@dabafinance/react-components';
import {useEffect, useState} from 'react';
import DBModal from 'components/modal/modal';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {
  CREATE_USER_INTERACTION,
  DELETE_USER_INTERACTION,
  UPDATE_USER_INTERACTION,
} from 'graphql/mutations/users';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import dotsIcon from 'images/dots.svg';
import {currencyCodes} from 'pages/adminOpportunities/stocksListings/create/CreateStock';
import {fieldRules, formatRoleString} from 'utils/helper';
import {currencySymbols} from 'utils/mock';
import {
  CloseCircleOutlined,
  MessageOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import FileUploadList from 'components/fileUploadList';
import InteractionDetailModal from './InteractionDetailModal';

export const UserInteractions = () => {
  const [openInteractionModal, setOpenInteractionModal] = useState({
    open: false,
    data: null,
  });
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    id: null,
  });
  const [openDetailModal, setOpenDetailModal] = useState({
    open: false,
    interactionId: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [form] = Form.useForm();
  const {id} = useParams();
  const outcomes = Form.useWatch('outcomes', form) || [];
  const depositStatus = Form.useWatch('depositStatus', form);
  const investmentStatus = Form.useWatch('investmentStatus', form);
  const dabaProStatus = Form.useWatch('dabaProStatus', form);
  const dabaAcademyStatus = Form.useWatch('dabaAcademyStatus', form);
  const type = Form.useWatch('type', form);

  const {data, refetch, loading} = useQuery(GET_USER_INTERACTIONS, {
    variables: {
      filter: {
        userId: id,
        paginate: true,
        page: 1,
        limit: 10,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const [addInteraction, {loading: loadingAdd}] = useMutation(
    CREATE_USER_INTERACTION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [deleteInteraction, {loading: loadingDelete}] = useMutation(
    DELETE_USER_INTERACTION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const [updateInteraction, {loading: loadingUpdate}] = useMutation(
    UPDATE_USER_INTERACTION,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );
  useEffect(() => {
    if (openInteractionModal.open && openInteractionModal.data) {
      const {date, note, type, outcomes, files} = openInteractionModal.data;
      const {
        kycStatus,
        depositStatus,
        investmentStatus,
        depositAmount,
        investmentAmount,
        investmentCurrency,
        dabaProStatus,
        dabaProPlan,
        dabaProCurrency,
        dabaAcademyStatus,
        dabaAcademyCurrency,
        dabaAcademyPlan,
        depositCurrency,
      } = outcomes || {};

      const validOutcomes = OUTCOMES.filter(outcome => {
        const value = outcomes?.[outcome?.name];
        const options = {
          kycStatus: KYC_STATUS_OPTIONS,
          depositStatus: DEPOSIT_STATUS_OPTIONS,
          investmentStatus: INVESTMENT_STATUS_OPTIONS,
          dabaProStatus: DABA_PRO_STATUS_OPTIONS,
          dabaAcademyStatus: DABA_ACADEMY_STATUS_OPTIONS,
        }[outcome.name];

        return value && options?.some(opt => opt.value === value);
      }).map(outcome => outcome.value);

      form.setFieldsValue({
        date: date ? moment(date) : null,
        note,
        type,
        outcomes: validOutcomes,
        kycStatus,
        depositStatus,
        investmentStatus,
        depositAmount,
        depositCurrency,
        investmentAmount,
        investmentCurrency,
        dabaProStatus,
        dabaProPlan,
        dabaProCurrency,
        dabaAcademyStatus,
        dabaAcademyCurrency,
        dabaAcademyPlan,
        files: Array.isArray(files) ? files : [],
      });
    }
  }, [openInteractionModal.open]);

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
        userId: id,
        paginate: true,
      },
    });
  };

  const columns = [
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      fixed: 'left',
      render: (text, record) => {
        return (
          <Tag color="default">
            {
              {
                CALL: (
                  <span>
                    {record?.type} (
                    <PhoneOutlined style={{fontSize: 14}} />)
                  </span>
                ),
                TEXT: (
                  <span>
                    {record?.type} (
                    <MessageOutlined style={{fontSize: 14}} />)
                  </span>
                ),
                BOTH: (
                  <span>
                    {record?.type} (
                    <MessageOutlined style={{fontSize: 14}} />,
                    <PhoneOutlined style={{fontSize: 14}} />)
                  </span>
                ),
                NO_ANSWER: (
                  <span style={{color: 'red'}}>
                    {record?.type?.replace('_', ' ')} (
                    <CloseCircleOutlined style={{fontSize: 14, color: 'red'}} />
                    )
                  </span>
                ),
              }[record?.type]
            }
          </Tag>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (text, record) => {
        return (
          <p className={styles.dateTimeContainer}>
            {moment(record?.date).format('DD, MMMM YYYY hh:mm a')}
          </p>
        );
      },
    },
    {
      title: 'Sales Agent',
      dataIndex: 'createdBy',
      render: (text, record) => {
        return record?.createdBy?.firstName ? (
          <p>
            {record?.createdBy?.firstName} {record?.createdBy?.lastName}
          </p>
        ) : (
          <p>{record?.createdBy?.email}</p>
        );
      },
    },
    {
      title: 'Deposit',
      dataIndex: 'depositStatus',
      render: (text, record) => {
        return (
          <Tag>
            {formatRoleString(record?.outcomes?.depositStatus) ?? '- -'}
            {record?.outcomes?.depositAmount ? (
              <span>
                - {currencySymbols[record?.outcomes?.depositCurrency]?.symbol}
                {record?.outcomes?.depositAmount?.toLocaleString()}
              </span>
            ) : null}
          </Tag>
        );
      },
    },
    {
      title: 'Investment',
      dataIndex: 'investmentStatus',
      render: (text, record) => {
        return (
          <Tag>
            {formatRoleString(record?.outcomes?.investmentStatus) ?? '- -'}
            {record?.outcomes?.investmentAmount ? (
              <span>
                -{' '}
                {currencySymbols[record?.outcomes?.investmentCurrency]?.symbol}
                {record?.outcomes?.investmentAmount?.toLocaleString()}
              </span>
            ) : null}
          </Tag>
        );
      },
    },
    {
      title: 'Daba Pro',
      dataIndex: 'dabaProStatus',
      render: (text, record) => {
        return (
          <Tag>
            {formatRoleString(record?.outcomes?.dabaProStatus) ?? '- -'}
            {record?.outcomes?.dabaProPlan ? (
              <span>- {formatRoleString(record?.outcomes?.dabaProPlan)}</span>
            ) : null}
          </Tag>
        );
      },
    },
    {
      title: 'Daba Academy',
      dataIndex: 'dabaAcademyStatus',
      render: (text, record) => {
        return (
          <Tag>
            {formatRoleString(record?.outcomes?.dabaAcademyStatus) ?? '- -'}
            {record?.outcomes?.dabaAcademyPlan ? (
              <span>
                - {formatRoleString(record?.outcomes?.dabaAcademyPlan)}
              </span>
            ) : null}
          </Tag>
        );
      },
    },
    {
      title: 'KYC',
      dataIndex: 'kycStatus',
      render: (text, record) => {
        return (
          <Tag>{formatRoleString(record?.outcomes?.kycStatus) ?? '- -'}</Tag>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      render: (text, record) => {
        return (
          <p>{moment(record?.createdAt).format('DD, MMMM YYYY hh:mm a')}</p>
        );
      },
    },
    {
      title: 'Actions',
      fixed: 'right',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  label: 'Edit',
                  key: 'edit',
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    setOpenInteractionModal({open: true, data: record});
                  },
                },
                {
                  label: 'Delete',
                  key: 'delete',
                  onClick: e => {
                    e.domEvent.stopPropagation();
                    setOpenDeleteModal({open: true, id: record.id});
                  },
                },
              ],
            }}
            trigger="click">
            <img onClick={e => e.stopPropagation()} src={dotsIcon} alt="more" />
          </Dropdown>
        );
      },
    },
  ];

  const handleCreateInteraction = values => {
    // eslint-disable-next-line no-unused-vars
    const {date, note, type, outcomes, files, ...rest} = values;

    const input = {
      user: id,
      type,
      date: moment(date).format(),
      note,
      ...(Array.isArray(files) && files.length > 0 && {files}),
      ...(type !== 'NO_ANSWER' && {
        outcomes: {
          ...rest,
          ...(rest.depositStatus === 'DEPOSIT_COMPLETED' && {
            depositAmount: rest.depositAmount,
          }),
          ...(rest.investmentStatus === 'INVESTMENT_MADE' && {
            investmentAmount: rest.investmentAmount,
          }),
          ...(rest.dabaProStatus === 'SOLD_A_SUBSCRIPTION' && {
            dabaProPlan: rest.dabaProPlan,
          }),
          ...(rest.dabaProStatus === 'SOLD_A_SUBSCRIPTION' && {
            dabaProCurrency: rest.dabaProCurrency,
          }),
          ...(rest.investmentStatus === 'INVESTMENT_MADE' && {
            investmentCurrency: rest.investmentCurrency,
          }),
          ...(rest.dabaAcademyStatus === 'SOLD_A_SUBSCRIPTION' && {
            dabaAcademyPlan: rest.dabaAcademyPlan,
          }),
          ...(rest.dabaAcademyStatus === 'SOLD_A_SUBSCRIPTION' && {
            dabaAcademyCurrency: rest.dabaAcademyCurrency,
          }),
        },
      }),
    };

    if (openInteractionModal.data) {
      updateInteraction({
        variables: {
          updateInteractionId: openInteractionModal.data.id,
          input,
        },
      })
        .then(({data: {updateInteraction}}) => {
          if (
            updateInteraction.statusCode === 400 ||
            updateInteraction.__typename === 'Error'
          ) {
            return toast.error(updateInteraction.message);
          } else {
            toast.success(updateInteraction.message);
            setOpenInteractionModal({open: false});
            form.resetFields();
            refetch();
          }
        })
        .catch(error => toast.error(error.message));
    } else {
      addInteraction({
        variables: {
          input,
        },
      })
        .then(({data: {addInteraction}}) => {
          if (
            addInteraction.statusCode === 400 ||
            addInteraction.__typename === 'Error'
          ) {
            return toast.error(addInteraction.message);
          } else {
            toast.success(addInteraction.message);
            setOpenInteractionModal({open: false});
            form.resetFields();
            refetch();
          }
        })
        .catch(error => toast.error(error.message));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Interactions History</h1>
          <p>Recent Customer Interaction History.</p>
        </div>
        <Button
          label="Create Interaction"
          onClick={() => setOpenInteractionModal({open: true})}
        />
      </div>
      <div>
        <Table
          columns={columns}
          onRow={record => ({
            style: {
              cursor: 'pointer',
            },
            onClick: () =>
              setOpenDetailModal({open: true, interactionId: record.id}),
          })}
          dataSource={data?.getInteractions?.data}
          loading={loading}
          className={styles.table}
          pagination={{
            current: currentPage,
            pageSize: limit,
            total: data?.getInteractions?.pagination?.total,
            onChange: handlePageChange,
            showSizeChanger: true,
          }}
        />
      </div>
      <DBModal
        isOpen={openInteractionModal.open}
        handleClose={() => {
          setOpenInteractionModal({open: false});
          form.resetFields();
        }}
        width={700}
        content={
          <div className={styles.createModal}>
            <div className={styles.createHeader}>
              <h1>Create Interaction</h1>
              <p>Create a new interaction for the user.</p>
            </div>
            <div>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleCreateInteraction}>
                <div className={styles.fieldsContainer}>
                  <Form.Item
                    rules={[fieldRules('Interaction Type is required')]}
                    label="Interaction Type"
                    className={styles.field}
                    name="type">
                    <Select>
                      {INTERACTION_TYPES.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label} ({option.icon})
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item
                    rules={[fieldRules('Date is required')]}
                    label="Date & Time"
                    name="date"
                    className={styles.field}>
                    <DatePicker showTime style={{width: 265}} />
                  </Form.Item>
                </div>
                {type !== 'NO_ANSWER' && (
                  <Form.Item label="Outcomes" name="outcomes">
                    <Select
                      placeholder="Select the Outcome of the Interaction"
                      mode="multiple"
                      allowClear>
                      {OUTCOMES.map(option => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                <div className={styles.fieldsContainer}>
                  {outcomes.includes('KYC_STATUS') && (
                    <Form.Item
                      className={styles.field}
                      label="KYC"
                      rules={[fieldRules('KYC is required')]}
                      name="kycStatus">
                      <Select allowClear>
                        {KYC_STATUS_OPTIONS.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {outcomes.includes('DEPOSIT_STATUS') && (
                    <Form.Item
                      className={styles.field}
                      label="Deposit"
                      rules={[fieldRules('Deposit is required')]}
                      name="depositStatus">
                      <Select allowClear>
                        {DEPOSIT_STATUS_OPTIONS.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {depositStatus === 'DEPOSIT_COMPLETED' && (
                    <>
                      <Form.Item
                        name="depositCurrency"
                        className={styles.field}
                        rules={[fieldRules('Currency is required')]}
                        label="Deposit Currency">
                        <Select allowClear>
                          {currencyCodes.map(option => (
                            <Select.Option
                              key={option.value}
                              value={option.value}>
                              {option.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        className={styles.field}
                        label="Deposit Amount"
                        rules={[fieldRules('Deposit Amount is required')]}
                        name="depositAmount">
                        <InputNumber
                          formatter={value =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          className="input-number"
                          placeholder="1000"
                        />
                      </Form.Item>
                    </>
                  )}
                  {outcomes.includes('INVESTMENT_STATUS') && (
                    <Form.Item
                      className={styles.field}
                      label="Investment"
                      rules={[fieldRules('Investment is required')]}
                      name="investmentStatus">
                      <Select allowClear>
                        {INVESTMENT_STATUS_OPTIONS.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {investmentStatus === 'INVESTMENT_MADE' && (
                    <>
                      <Form.Item
                        className={styles.field}
                        rules={[fieldRules('Investment Amount is required')]}
                        label="Investment Amount"
                        name="investmentAmount">
                        <InputNumber
                          formatter={value =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          className="input-number"
                          placeholder="1000"
                        />
                      </Form.Item>
                      <Form.Item
                        className={styles.field}
                        label="Investment Currency"
                        name="investmentCurrency">
                        <Select allowClear>
                          {currencyCodes.map(option => (
                            <Select.Option
                              key={option.value}
                              value={option.value}>
                              {option.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </>
                  )}
                  {outcomes.includes('DABA_PRO_STATUS') && (
                    <>
                      <Form.Item
                        className={styles.field}
                        rules={[fieldRules('Daba Pro is required')]}
                        label="Daba Pro"
                        name="dabaProStatus">
                        <Select allowClear>
                          {DABA_PRO_STATUS_OPTIONS.map(option => (
                            <Select.Option
                              key={option.value}
                              value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {dabaProStatus === 'SOLD_A_SUBSCRIPTION' && (
                        <>
                          <Form.Item
                            name="dabaProCurrency"
                            className={styles.field}
                            rules={[
                              fieldRules('Daba Pro Currency is required'),
                            ]}
                            label="Currency Plan was sold in">
                            <Select allowClear>
                              {currencyCodes.map(option => (
                                <Select.Option
                                  key={option.value}
                                  value={option.value}>
                                  {option.value}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            className={styles.field}
                            rules={[fieldRules('Daba Pro Plan is required')]}
                            label="Daba Pro Plan"
                            name="dabaProPlan">
                            <Select allowClear>
                              {DABA_PRO_PLAN.map(option => (
                                <Select.Option
                                  key={option.value}
                                  value={option.value}>
                                  {option.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </>
                      )}
                    </>
                  )}
                  {outcomes.includes('DABA_ACADEMY_STATUS') && (
                    <Form.Item
                      className={styles.field}
                      label="Daba Academy"
                      name="dabaAcademyStatus">
                      <Select allowClear>
                        {DABA_ACADEMY_STATUS_OPTIONS.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {dabaAcademyStatus === 'SOLD_A_SUBSCRIPTION' && (
                    <Form.Item
                      className={styles.field}
                      label="Currency Sold in"
                      name="dabaAcademyCurrency">
                      <Select allowClear>
                        {currencyCodes.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.value}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {dabaAcademyStatus === 'SOLD_A_SUBSCRIPTION' && (
                    <Form.Item
                      className={styles.field}
                      label="Daba Academy Plan"
                      name="dabaAcademyPlan">
                      <Select allowClear>
                        {DABA_ACADEMY_PLAN.map(option => (
                          <Select.Option
                            key={option.value}
                            value={option.value}>
                            {option.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </div>
                <Form.Item label="Notes" name="note">
                  <Input.TextArea
                    placeholder="Write Notes from the Interaction"
                    rows={4}
                    maxLength={1000}
                  />
                </Form.Item>
                <FileUploadList
                  form={form}
                  name="files"
                  label="Files"
                  bucket="campaign-bucket-images"
                  initialFiles={
                    openInteractionModal.data?.files
                      ? openInteractionModal.data.files
                      : null
                  }
                />
                <SubmitButton
                  disabled={loadingAdd || loadingUpdate}
                  label={
                    openInteractionModal.data
                      ? 'Update Interaction'
                      : 'Create Interaction'
                  }
                />
              </Form>
            </div>
          </div>
        }
      />
      <InteractionDetailModal
        isOpen={openDetailModal.open}
        onClose={() => setOpenDetailModal({open: false, interactionId: null})}
        interaction={
          data?.getInteractions?.data?.find(
            i => i.id === openDetailModal.interactionId,
          ) || null
        }
        onRefetch={refetch}
      />
      <ConfirmModal
        openModal={openDeleteModal.open}
        setOpenModal={() => setOpenDeleteModal({open: false})}
        title="Delete Interaction"
        confirmText="Are you sure you want to delete this interaction?"
        loading={loadingDelete}
        handleOperation={() =>
          deleteInteraction({
            variables: {
              deleteInteractionByIdId: openDeleteModal.id,
            },
          })
            .then(({data: {deleteInteractionById}}) => {
              if (
                deleteInteractionById.statusCode === 400 ||
                deleteInteractionById.__typename === 'Error'
              ) {
                return toast.error(deleteInteractionById.message);
              } else {
                toast.success(deleteInteractionById.message);
                setOpenDeleteModal({open: false});
                refetch();
              }
            })
            .catch(error => toast.error(error.message))
        }
      />
    </div>
  );
};

const INTERACTION_TYPES = [
  {value: 'CALL', label: 'Call', icon: <PhoneOutlined />},
  {value: 'TEXT', label: 'Text', icon: <MessageOutlined />},
  {
    value: 'BOTH',
    label: 'Both',
    icon: (
      <>
        <MessageOutlined />, <PhoneOutlined />
      </>
    ),
  },
  {
    value: 'NO_ANSWER',
    label: 'No Answer',
    icon: <CloseCircleOutlined style={{color: 'red'}} />,
  },
];

const DABA_ACADEMY_PLAN = [
  {value: 'ONLINE_COURSE', label: 'Online Course'},
  {value: 'PDF_BOOK', label: 'PDF Book'},
  {value: 'FREE_CHAPTER', label: 'Free Chapter'},
];

const KYC_STATUS_OPTIONS = [
  {value: 'KYC_ATTEMPTED', label: 'Kyc Attempted'},
  {value: 'KYC_COMPLETED', label: 'Kyc Completed'},
  {value: 'KYC_FAILED', label: 'Kyc Failed'},
  {value: 'NO_ATTEMPT', label: 'No Attempt'},
];

const DEPOSIT_STATUS_OPTIONS = [
  {value: 'DEPOSIT_ATTEMPTED', label: 'Attempted to deposit'},
  {value: 'DEPOSIT_COMPLETED', label: 'Deposit Completed'},
  {value: 'DEPOSIT_FAILED', label: 'Deposit Failed'},
  {value: 'NO_ATTEMPT', label: 'No Attempt'},
];

const INVESTMENT_STATUS_OPTIONS = [
  {value: 'NO_INVESTMENT_ATTEMPT', label: 'No Attempt to invest'},
  {value: 'INVESTMENT_ATTEMPT', label: 'Attempted to invest'},
  {value: 'INVESTMENT_MADE', label: 'Investment Made'},
  {value: 'NO_INVESTMENT_MADE', label: 'No Investment Made'},
];

const DABA_ACADEMY_STATUS_OPTIONS = [
  {value: 'DABA_ACADEMY_ATTEMPT', label: 'Attempted to sell Daba Academy'},
  {value: 'SOLD_A_SUBSCRIPTION', label: 'Sold a Subscription'},
  {value: 'NO_ATTEMPT', label: 'No Attempt to sell Daba Academy'},
  {value: 'NO_SALE', label: 'No Sale'},
];

const DABA_PRO_STATUS_OPTIONS = [
  {value: 'DABA_PRO_ATTEMPT', label: 'Attempted to sell Daba Pro'},
  {value: 'SOLD_A_SUBSCRIPTION', label: 'Sold a Subscription'},
  {value: 'NO_SALE', label: 'No Sale'},
  {value: 'NO_ATTEMPT', label: 'No Attempt to sell Daba Pro'},
];

const OUTCOMES = [
  {value: 'KYC_STATUS', label: 'KYC', name: 'kycStatus'},
  {value: 'DEPOSIT_STATUS', label: 'Deposit', name: 'depositStatus'},
  {
    value: 'INVESTMENT_STATUS',
    label: 'Investment',
    name: 'investmentStatus',
  },
  {value: 'DABA_PRO_STATUS', label: 'Daba Pro', name: 'dabaProStatus'},
  {
    value: 'DABA_ACADEMY_STATUS',
    label: 'Daba Academy',
    name: 'dabaAcademyStatus',
  },
];

export const DABA_PRO_PLAN = [
  {value: 'THREE_MONTHS', label: '3 Months'},
  {value: 'SIX_MONTHS', label: '6 Months'},
  {value: 'ONE_YEAR', label: '1 Year'},
];
